/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { CustomFilterV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsNsir from '../../../store/nsir/actions';
import * as Role from '../../../constants/role';
import { canViewDigitadorReport } from '../../../helpers/digitadorReportPermission';
import TableService from '../../../services/TableService';
import BaseService from '../../../services/BaseService';
import KpiStrip from './KpiStrip';
import LenteSegment from './LenteSegment';
import GeralDualCards, { DualCard } from './GeralDualCards';
import EquipeTable from './EquipeTable';
import RankingCharts from './RankingCharts';
import LenteLookupTable from './LenteLookupTable';
import ExportFilterDialog from './ExportFilterDialog';
import { countDiasUteisNoIntervalo } from '../Calendario/dateUtils';
import {
  LENTE_IDS,
  FERIADO_TIPOS_GLOBAIS,
  FERIADO_UF_OPERACAO,
  ADERE_LENTES,
  attachAdereToEquipeRows,
  attachPorGerencia,
  deriveEquipeFromItems,
  formatAtualizadoExact,
  formatAtualizadoHa,
  formatMedicaoPeriodLabel,
  getCurrentAdereMes,
  getLenteLabel,
  getMedicaoPeriod,
  getPeriodoPrefix,
  getPreviousMedicaoPeriod,
  isMedicaoPeriod,
  mergeEquipeRows,
  mergeResumoAdminPair,
  normalizeAdminExportForApi,
  normalizeAdereResumoResponse,
  normalizeByDigitadorResumo,
  normalizeFilterForLente,
  normalizeResumoAdmin,
  openDigitadorDrill,
  openNsirsCargaDrill,
  parseLenteIdFromSearch,
  persistLenteId,
  readInitialLenteId,
  writeLenteIdToSearch,
} from './commandCenterConstants';

const getDefaultFilter = () => {
  const { inicio, fim } = getMedicaoPeriod();
  return {
    inicio,
    fim,
    dateTypes: ['DATA_DIGITACAO'],
    digitadorId: [],
  };
};

const fetchChart = async (url, body) => {
  try {
    const response = await BaseService.post({ url, data: body });
    return response?.data?.items || [];
  } catch {
    return [];
  }
};

const fetchResumoSafe = async (body) => {
  try {
    const response = await TableService.request('reports/digitacao/resumo', body);
    return normalizeResumoAdmin(response?.data);
  } catch {
    return normalizeResumoAdmin(null);
  }
};

const fetchFeriadosGlobais = async (inicio, fim) => {
  const start = moment(inicio);
  const end = moment(fim);
  const years = Array.from(new Set([start.year(), end.year()]));
  const lists = await Promise.all(years.map(async (ano) => {
    const mesInicio = ano === start.year() ? start.month() + 1 : 1;
    const mesFim = ano === end.year() ? end.month() + 1 : 12;
    const res = await TableService.request('calendario/filter?p=0&s=50', {
      ano,
      mesInicio,
      mesFim,
      uf: FERIADO_UF_OPERACAO,
      tipos: FERIADO_TIPOS_GLOBAIS,
      statuses: ['VALIDO'],
    });
    const data = res?.data;
    if (Array.isArray(data)) return data;
    return data?.content ?? data?.rows ?? [];
  }));
  return lists.flat();
};

const RelatorioDigitacaoV2 = ({
  authData,
  generateReportDigitadorDiario,
}) => {
  const history = useHistory();
  const location = useLocation();
  const defaultFilter = useMemo(() => getDefaultFilter(), []);
  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [lenteId, setLenteId] = useState(readInitialLenteId);
  const [openPeriod, setOpenPeriod] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [reload, setReload] = useState(false);
  const [diasUteisRestantes, setDiasUteisRestantes] = useState(null);
  const [resumoByLente, setResumoByLente] = useState({
    [LENTE_IDS.MT_BT]: normalizeResumoAdmin(null),
    [LENTE_IDS.USO_MUTUO]: normalizeResumoAdmin(null),
  });
  const [bundleLoading, setBundleLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [nowTick, setNowTick] = useState(0);
  const skipSearchSyncRef = useRef(true);

  const [mtbtCharts, setMtbtCharts] = useState({ prod: [], carga: [] });
  const [umCharts, setUmCharts] = useState({ prod: [], carga: [] });
  const [mtbtEquipe, setMtbtEquipe] = useState(null);
  const [umEquipe, setUmEquipe] = useState(null);
  const [equipeRows, setEquipeRows] = useState([]);
  const [adereByLente, setAdereByLente] = useState({});

  const canScopeDigitadores = canViewDigitadorReport({ authData });

  const periodoLabel = useMemo(() => {
    const range = formatMedicaoPeriodLabel(localFilter.inicio, localFilter.fim);
    if (!range) return null;
    return `${getPeriodoPrefix(localFilter.inicio, localFilter.fim)} ${range}`;
  }, [localFilter.inicio, localFilter.fim]);

  const periodoEncerrado = useMemo(() => {
    if (localFilter.fim == null) return false;
    return moment(localFilter.fim).startOf('day').isBefore(moment().startOf('day'), 'day');
  }, [localFilter.fim]);

  const fetchBundle = useCallback(async () => {
    setBundleLoading(true);
    try {
      const mtbtBody = normalizeFilterForLente(localFilter, LENTE_IDS.MT_BT);
      const umBody = normalizeFilterForLente(localFilter, LENTE_IDS.USO_MUTUO);
      const bodyBase = {
        inicio: localFilter.inicio,
        fim: localFilter.fim,
        dateTypes: ['DATA_DIGITACAO'],
        digitadorIds: Array.isArray(localFilter.digitadorId)
          ? localFilter.digitadorId
          : [],
      };
      const mes = getCurrentAdereMes();

      const [
        mtbtResumo,
        umResumo,
        mtbtProd,
        mtbtCarga,
        umProd,
        umCarga,
        aderePairs,
        equipeResumoResp,
        feriados,
      ] = await Promise.all([
        fetchResumoSafe(mtbtBody),
        fetchResumoSafe(umBody),
        fetchChart('reports/digitacao/charts/by-digitador', mtbtBody),
        fetchChart('reports/digitacao/charts/by-digitador-carga', mtbtBody),
        fetchChart('reports/digitacao/charts/by-digitador', umBody),
        fetchChart('reports/digitacao/charts/by-digitador-carga', umBody),
        Promise.all(ADERE_LENTES.map(async (id) => {
          try {
            const res = await BaseService.get({
              url: `reports/digitacao/adere/resumo?mes=${encodeURIComponent(mes)}&lenteId=${encodeURIComponent(id)}`,
            });
            return [id, normalizeAdereResumoResponse(res?.data)];
          } catch {
            return [id, null];
          }
        })),
        BaseService.post({
          url: 'reports/digitacao/charts/by-digitador-resumo',
          data: bodyBase,
        }).catch(() => null),
        (async () => {
          const fim = localFilter.fim;
          if (fim == null) return { restantes: null };
          const today = moment().startOf('day');
          const end = moment(fim).startOf('day');
          if (end.isBefore(today, 'day')) return { restantes: 0 };
          try {
            const list = await fetchFeriadosGlobais(today.valueOf(), fim);
            const counted = countDiasUteisNoIntervalo(
              today,
              end,
              list,
              FERIADO_TIPOS_GLOBAIS,
            );
            return { restantes: counted.diasUteis };
          } catch {
            return { restantes: null };
          }
        })(),
      ]);

      setResumoByLente({
        [LENTE_IDS.MT_BT]: mtbtResumo,
        [LENTE_IDS.USO_MUTUO]: umResumo,
      });
      setMtbtCharts({ prod: mtbtProd, carga: mtbtCarga });
      setUmCharts({ prod: umProd, carga: umCarga });
      setMtbtEquipe(deriveEquipeFromItems(mtbtProd, mtbtCarga, LENTE_IDS.MT_BT));
      setUmEquipe(deriveEquipeFromItems(umProd, umCarga, LENTE_IDS.USO_MUTUO));
      setAdereByLente(Object.fromEntries(aderePairs));
      setDiasUteisRestantes(feriados.restantes);

      const normalized = normalizeByDigitadorResumo(equipeResumoResp?.data);
      if (normalized.rows.length) {
        setEquipeRows(attachPorGerencia(normalized.rows, mtbtCarga, umCarga));
      } else {
        setEquipeRows(mergeEquipeRows({
          mtbtProd,
          mtbtCarga,
          umProd,
          umCarga,
        }));
      }
      setLastUpdatedAt(Date.now());
    } catch {
      // Mantém dados e stamp anteriores — não mente a hora.
    } finally {
      setBundleLoading(false);
    }
  }, [localFilter, reload]);

  useEffect(() => {
    fetchBundle();
  }, [fetchBundle]);

  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const next = writeLenteIdToSearch(location.search, lenteId);
    if ((location.search || '') !== next) {
      history.replace(`${location.pathname}${next}`);
    }
    // Só o segmento dispara escrita; popstate entra pelo efeito de search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lenteId]);

  useEffect(() => {
    if (skipSearchSyncRef.current) {
      skipSearchSyncRef.current = false;
      return;
    }
    const fromUrl = parseLenteIdFromSearch(location.search);
    setLenteId((prev) => {
      if (prev === fromUrl) return prev;
      persistLenteId(fromUrl);
      return fromUrl;
    });
  }, [location.search]);

  const resumoGeral = useMemo(
    () => mergeResumoAdminPair(
      resumoByLente[LENTE_IDS.MT_BT],
      resumoByLente[LENTE_IDS.USO_MUTUO],
    ),
    [resumoByLente],
  );

  const resumoLente = lenteId === LENTE_IDS.GERAL
    ? resumoGeral
    : (resumoByLente[lenteId] || normalizeResumoAdmin(null));

  const porLenteMtbt = useMemo(() => {
    const fromApi = (resumoByLente[LENTE_IDS.MT_BT]?.porLente || [])
      .find((p) => p.lenteId === LENTE_IDS.MT_BT);
    const base = fromApi || mtbtEquipe;
    if (!base) return base;
    return { ...base, adere: adereByLente[LENTE_IDS.MT_BT]?.equipe || null };
  }, [resumoByLente, mtbtEquipe, adereByLente]);

  const porLenteUm = useMemo(() => {
    const fromApi = (resumoByLente[LENTE_IDS.USO_MUTUO]?.porLente || [])
      .find((p) => p.lenteId === LENTE_IDS.USO_MUTUO);
    const base = fromApi || umEquipe;
    if (!base) return base;
    return { ...base, adere: adereByLente[LENTE_IDS.USO_MUTUO]?.equipe || null };
  }, [resumoByLente, umEquipe, adereByLente]);

  const equipeRowsWithAdere = useMemo(
    () => attachAdereToEquipeRows(equipeRows, adereByLente),
    [equipeRows, adereByLente],
  );

  const lenteCharts = lenteId === LENTE_IDS.USO_MUTUO ? umCharts : mtbtCharts;
  const lenteMetrics = lenteId === LENTE_IDS.USO_MUTUO ? porLenteUm : porLenteMtbt;

  const onExport = (filterData) => {
    generateReportDigitadorDiario(normalizeAdminExportForApi(filterData));
  };

  const showRestoreMedicao = !isMedicaoPeriod(localFilter.inicio, localFilter.fim);
  const showCicloAnterior = isMedicaoPeriod(localFilter.inicio, localFilter.fim);

  const applyPeriod = (inicio, fim) => {
    setLocalFilter({
      ...getDefaultFilter(),
      inicio,
      fim,
    });
    setReload((prev) => !prev);
  };

  const onRestoreMedicao = () => {
    applyPeriod(defaultFilter.inicio, defaultFilter.fim);
  };

  const onCicloAnterior = () => {
    const prev = getPreviousMedicaoPeriod();
    applyPeriod(prev.inicio, prev.fim);
  };

  const onRefresh = () => {
    if (bundleLoading) return;
    setReload((prev) => !prev);
  };

  const handleLenteChange = (nextLenteId) => {
    setLenteId(nextLenteId);
    persistLenteId(nextLenteId);
  };

  const handleSelectDigitador = ({ digitadorId, nome }) => {
    openDigitadorDrill({ digitadorId, nome });
  };

  const handleSelectGerencia = (row) => {
    openNsirsCargaDrill({ malhaId: row.gerenciaId });
  };

  const atualizadoLabel = useMemo(
    () => (bundleLoading ? 'Atualizando…' : formatAtualizadoHa(lastUpdatedAt)),
    [bundleLoading, lastUpdatedAt, nowTick],
  );

  if (authData?.role === Role.DIGITADOR) {
    return <Redirect to="/v2/minha-digitacao" />;
  }

  if (!canViewDigitadorReport({ authData })) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Você não tem permissão para o command center de digitação.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-start' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box>
            <Typography variant="h4">Painel Digitação</Typography>
            {authData?.nome && (
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                Olá, {authData.nome}
              </Typography>
            )}
            {periodoLabel && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <Typography variant="body2" color="text.secondary">
                  {periodoLabel}
                  {resumoGeral?.equipe?.diasUteisGlobais != null
                    ? ` · ciclo de ${resumoGeral.equipe.diasUteisGlobais} dias úteis`
                    : ''}
                </Typography>
                <Tooltip
                  title="Pode variar de digitador para digitador por causa dos feriados municipais e os aniversários."
                >
                  <IconButton size="small" aria-label="Sobre o ciclo de dias úteis">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            {periodoEncerrado ? (
              <Typography variant="body2" color="text.secondary">
                Período encerrado
              </Typography>
            ) : diasUteisRestantes != null && (
              <Typography variant="body2" color="text.secondary">
                {diasUteisRestantes === 1
                  ? 'Falta 1 dia útil para o fim do período'
                  : `Faltam ${diasUteisRestantes} dias úteis para o fim do período`}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.5, ml: -0.75 }}
            >
              {showCicloAnterior && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<HistoryOutlinedIcon />}
                  onClick={onCicloAnterior}
                >
                  Ciclo anterior
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                startIcon={<DateRangeOutlinedIcon />}
                onClick={() => setOpenPeriod(true)}
              >
                Escolher período
              </Button>
              {showRestoreMedicao && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<RestartAltOutlinedIcon />}
                  onClick={onRestoreMedicao}
                >
                  Medição atual
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                startIcon={<RefreshOutlinedIcon />}
                onClick={onRefresh}
                disabled={bundleLoading}
              >
                Atualizar
              </Button>
            </Stack>
            {atualizadoLabel && (
              <Tooltip title={lastUpdatedAt ? formatAtualizadoExact(lastUpdatedAt) : ''}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {atualizadoLabel}
                </Typography>
              </Tooltip>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => setOpenExport(true)}
          >
            Exportar
          </Button>
        </Stack>

        <LenteSegment value={lenteId} onChange={handleLenteChange} />

        {lenteId === LENTE_IDS.GERAL ? (
          <>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', lg: 'stretch' }}
              useFlexGap
              flexWrap="wrap"
            >
              <GeralDualCards
                mtbt={porLenteMtbt}
                usoMutuo={porLenteUm}
                loading={bundleLoading}
                onEnterLente={handleLenteChange}
              />
              <KpiStrip
                compact
                vertical
                clickable={false}
                totalPosition="last"
                resumo={resumoGeral}
                loading={bundleLoading}
              />
            </Stack>
            <EquipeTable
              rows={equipeRowsWithAdere}
              loading={bundleLoading}
            />
          </>
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', lg: 'stretch' }}
              useFlexGap
              flexWrap="wrap"
            >
              <DualCard
                title={getLenteLabel(lenteId)}
                lenteId={lenteId}
                metrics={lenteMetrics}
                loading={bundleLoading}
                interactive={false}
              />
              <KpiStrip
                compact
                vertical
                clickable={false}
                totalPosition="last"
                resumo={resumoLente}
                loading={bundleLoading}
              />
            </Stack>
            <RankingCharts
              cargaItems={lenteCharts.carga}
              lenteId={lenteId}
              loading={bundleLoading}
              onSelectDigitador={handleSelectDigitador}
              onSelectGerencia={handleSelectGerencia}
            />
            <LenteLookupTable
              prodItems={lenteCharts.prod}
              cargaItems={lenteCharts.carga}
              lenteId={lenteId}
              loading={bundleLoading}
              adereResumo={adereByLente[lenteId]}
            />
          </>
        )}

        <CustomFilterV2
          variant="dialog"
          open={openPeriod}
          onClose={() => setOpenPeriod(false)}
          title="Escolher período"
          fullWidth
          maxWidth="sm"
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          confirmButtonText="Aplicar"
          fields={[
            { field: 'useDates', useDateTypes: true, useDateTypesReadOnly: true },
          ]}
          onFilter={(filterData) => {
            applyPeriod(
              filterData.inicio ?? defaultFilter.inicio,
              filterData.fim ?? defaultFilter.fim,
            );
            setOpenPeriod(false);
          }}
        />
        <ExportFilterDialog
          open={openExport}
          onClose={() => setOpenExport(false)}
          defaultFilter={localFilter}
          canScopeDigitadores={canScopeDigitadores}
          onExport={onExport}
        />
      </Stack>
    </Box>
  );
};

RelatorioDigitacaoV2.propTypes = {
  authData: PropTypes.object.isRequired,
  generateReportDigitadorDiario: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      generateReportDigitadorDiario: actionsNsir.generateReportDigitadorDiario,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RelatorioDigitacaoV2);
